// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { InsightsResponse } from "@bitebalance/contracts"
import { motion, useReducedMotion, useSpring, useTransform } from "framer-motion"
import { useEffect } from "react"
import { useI18n, formatEurFromCents } from "@/i18n"
import { EstimatedBadge, EmptyState } from "@/components/state"

export interface ValueCardProps {
  insights: InsightsResponse | null
}

const KIND_LABEL_KEYS: Record<string, string> = {
  pantry_reuse: "views.valueCard.byKindPantryReuse",
  offer: "views.valueCard.byKindOffer",
}

/** Per-kind split of realized savings (C-042/PRD §7.10). Only real, earned
 * kinds appear; an empty or null breakdown renders nothing. */
function ByKindBreakdown({ byKind }: { byKind: InsightsResponse["byKind"] }) {
  const { t, locale } = useI18n()
  if (!byKind) return null
  const rows = Object.entries(byKind).filter(([, cents]) => typeof cents === "number" && cents > 0)
  if (rows.length === 0) return null
  return (
    <div className="mt-2">
      <p className="text-xs uppercase text-ink-2">{t("views.valueCard.byKindHeading")}</p>
      <ul className="mt-1 flex flex-col gap-0.5">
        {rows.map(([kind, cents]) => (
          <li key={kind} className="flex justify-between text-sm text-ink-2">
            <span>{t(KIND_LABEL_KEYS[kind] ?? kind)}</span>
            <span className="tabular-nums">{formatEurFromCents(cents as number, locale)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** Today's realized-vs-projected value card (T12): honest empty state, never €0-as-certainty. */
/** Animated euro readout — the amount counts up on first view (owner
 * 2026-08-30: the value card felt static). Reduced-motion renders the
 * final number instantly; the value itself is always the server's. */
function CountUpEur({ cents }: { cents: number }) {
  const { locale } = useI18n()
  const reduce = useReducedMotion()
  const spring = useSpring(reduce ? cents : 0, { stiffness: 90, damping: 24 })
  const text = useTransform(spring, v => formatEurFromCents(Math.round(v), locale))
  useEffect(() => {
    spring.set(cents)
  }, [spring, cents])
  if (reduce) return <span>{formatEurFromCents(cents, locale)}</span>
  // The REAL value lives in an sr-only span (stable for screen readers and
  // tests); the counting number is purely visual.
  return (
    <>
      <span className="sr-only">{formatEurFromCents(cents, locale)}</span>
      <motion.span aria-hidden="true">{text}</motion.span>
    </>
  )
}

export function ValueCard({ insights }: ValueCardProps) {
  const { t } = useI18n()

  if (!insights || !insights.hasData) {
    return (
      <EmptyState title={t("views.valueCard.emptyTitle")} body={t("views.valueCard.emptyBody")} />
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="flex flex-col gap-3 rounded-bb-xl border border-line bg-paper p-4 shadow-sm transition-shadow hover:shadow-md"
    >
      <h2 className="font-semibold text-ink">{t("views.valueCard.title")}</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <p className="text-xs uppercase text-ink-2">{t("views.valueCard.realizedLabel")}</p>
          {insights.realized ? (
            <>
              <p className="flex items-center gap-2 text-lg font-semibold text-ink">
                <CountUpEur cents={insights.realized.amountCents} />
                <EstimatedBadge />
              </p>
              <ByKindBreakdown byKind={insights.byKind} />
            </>
          ) : (
            <p className="text-sm text-ink-2">{t("views.valueCard.emptyBody")}</p>
          )}
        </div>
        <div>
          <p className="text-xs uppercase text-ink-2">{t("views.valueCard.projectedLabel")}</p>
          {insights.projected ? (
            <p className="flex items-center gap-2 text-lg font-semibold text-ink">
              <CountUpEur cents={insights.projected.amountCents} />
              <EstimatedBadge />
            </p>
          ) : (
            <p className="text-sm text-ink-2">—</p>
          )}
        </div>
      </div>
      {insights.adherence && (
        <p className="text-sm text-ink-2">
          {t("views.valueCard.adherenceLabel", {
            recorded: insights.adherence.outcomesRecorded,
            planned: insights.adherence.mealsPlanned,
          })}
        </p>
      )}
    </motion.div>
  )
}
